import React from 'react'
import { NightlyButton } from '../../../buttons/NightlyButton/NightlyButton'
import {
    StyledNightlyEditionContainer,
    StyledNightlyEditionHeading,
    StyledNightlyEditionTextContainer,
    StyledNightlyEditionSubHeading,
    StyledNightlyEditionTeaser,
} from '../SpecificEdition/NightlySpecificEdition.styled'
import {
    HeaderContainer,
    StyledDivider,
    StyledNightlyEditionButtonContainerLeft,
    StyledNightlyEditionContentHome,
    StyledNightlyEditionPictureRight,
    StyledNightlyEditionTextContainerLeft,
} from './SpecialEditionHome.styled'
import { HeaderPrefix } from '../SpecialEdition/SpecialEdition.styled'
import {
    AllEvents,
    createEditionSelectItemEvent,
    Product,
    scaleImage,
    WebLink,
} from '@news-mono/web-common'
import { EditionV4DTO } from '@west-australian-newspapers/publication-types'
import { breakpointMax, breakpoint } from '../../../__styling'

interface TheNightlyOnHomeProps {
    title: string
    teaser: string
    link: string
    imageURL: string
    lazyloadImages?: boolean
    onEvent: (event: AllEvents) => void
}

/**
 * @deprecated - This component uses a hard-coded list of "The Nightly On" editions
 * `apps/thenightly/src/app/routes/thenightly-on/onEditionsConfig.ts`
 * to render the latest edition on the homepage CTA
 * This component is to be removed once "The Nightly On" editions are published via CUE and replaced with the
 * `SpecialEditionHome` component which allows for more dynamic application.
 */
export const TheNightlyOnHome = ({
    title,
    teaser,
    link,
    imageURL,
    lazyloadImages,
    onEvent,
}: TheNightlyOnHomeProps) => {
    const handleImageClick = () => {
        onEvent &&
            onEvent(
                createEditionSelectItemEvent({
                    edition: {
                        id: title,
                        heading: title,
                    },
                    collectionName: 'TheNightlyOn_ImageButton',
                    product: Product.TheNightly,
                    itemDetails: {
                        category: 'thenightly_on_cta',

                        linkText: 'ImageClicked',
                        linkUrl: `/on/${title.toLowerCase().replace(' ', '-')}`,
                    },
                }),
            )
    }

    const moreArticlesPath = `/on/${title.toLowerCase().replace(' ', '-')}`

    return (
        <>
            <StyledDivider />
            <StyledNightlyEditionContainer style={{ paddingBottom: 0 }}>
                <StyledNightlyEditionContentHome>
                    <HeaderContainer>
                        <HeaderPrefix>The Nightly On</HeaderPrefix>
                        <StyledNightlyEditionHeading>
                            {title} Edition
                        </StyledNightlyEditionHeading>
                    </HeaderContainer>
                    <StyledNightlyEditionTextContainerLeft>
                        <StyledNightlyEditionSubHeading>
                            In this Edition
                        </StyledNightlyEditionSubHeading>
                        <StyledNightlyEditionTeaser>
                            {teaser}
                        </StyledNightlyEditionTeaser>
                    </StyledNightlyEditionTextContainerLeft>
                    <StyledNightlyEditionButtonContainerLeft>
                        <NightlyButton
                            variant={'default'}
                            text={`${title} Edition`}
                            action={{
                                type: 'link',
                                to: link,
                                onClick: () =>
                                    onEvent &&
                                    onEvent(
                                        createEditionSelectItemEvent({
                                            edition: {
                                                id: title,
                                                heading: title,
                                            },
                                            collectionName:
                                                'TheNightlyOn_ReadEditionButton',
                                            product: Product.TheNightly,
                                            itemDetails: {
                                                category: 'thenightly_on_cta',

                                                linkText: `${title} Edition`,
                                                linkUrl: link,
                                            },
                                        }),
                                    ),
                            }}
                            color={'primary'}
                            fill={'filled'}
                            layout={{
                                shouldGrow: true,
                                alignSelf: 'stretch',
                            }}
                        />
                        <NightlyButton
                            variant={'default'}
                            text={'More Articles'}
                            action={{
                                type: 'link',
                                to: moreArticlesPath,
                                onClick: () =>
                                    onEvent &&
                                    onEvent(
                                        createEditionSelectItemEvent({
                                            edition: {
                                                id: title,
                                                heading: title,
                                            },
                                            collectionName:
                                                'TheNightlyOn_MoreArticlesButton',
                                            product: Product.TheNightly,
                                            itemDetails: {
                                                category: 'thenightly_on_cta',

                                                linkText: 'More Articles',
                                                linkUrl: moreArticlesPath,
                                            },
                                        }),
                                    ),
                            }}
                            color={'primary'}
                            fill={'outline'}
                            layout={{
                                shouldGrow: true,
                                alignSelf: 'stretch',
                            }}
                        />
                    </StyledNightlyEditionButtonContainerLeft>
                    <StyledNightlyEditionTextContainer></StyledNightlyEditionTextContainer>
                    <StyledNightlyEditionPictureRight>
                        <WebLink to={link} onClick={handleImageClick}>
                            <img
                                loading={lazyloadImages ? 'lazy' : undefined}
                                src={imageURL}
                                onError={({ currentTarget }) => {
                                    currentTarget.onerror = null
                                    currentTarget.src = require('../../ePaper/assets/paper.jpg')
                                }}
                                alt={'The Nightly Special Edition'}
                            />
                        </WebLink>
                    </StyledNightlyEditionPictureRight>
                </StyledNightlyEditionContentHome>
            </StyledNightlyEditionContainer>
        </>
    )
}

export interface SpecialEditionHomeProps {
    edition: EditionV4DTO
    publicationTitle: string
    lazyloadImages?: boolean
    onEvent: (event: AllEvents) => void
}

export const SpecialEditionHome = ({
    edition,
    publicationTitle,
    lazyloadImages,
    onEvent,
}: SpecialEditionHomeProps) => {
    const mainImage = edition.mainImages && {
        img: scaleImage(edition.mainImages[0].src, 600, false),
        alt: edition.mainImages[0].altText ?? edition.mainImages[0].captionText,
    }

    // The title for "The Nightly On" editions is in the format "title_DD-MM-YYYY", this
    // removes the date part and normalizes the title to be capitalized.
    const normalizedEditionTitle = edition.title
        ? edition.title.split('_')[0].charAt(0).toUpperCase() +
          edition.title.split('_')[0].slice(1).toLowerCase()
        : ''

    const handleImageClick = () => {
        onEvent &&
            onEvent(
                createEditionSelectItemEvent({
                    edition,
                    collectionName: 'TheNightlyOn_ImageButton',
                    product: Product.TheNightly,
                    itemDetails: {
                        category: 'thenightly_on_cta',

                        linkText: 'Image Clicked',
                        linkUrl: edition.primaryTopic
                            ? `${edition.primaryTopic}`
                            : '/on',
                    },
                }),
            )
    }

    return (
        <>
            <StyledDivider />
            <StyledNightlyEditionContainer style={{ paddingBottom: 0 }}>
                <StyledNightlyEditionContentHome>
                    <HeaderContainer>
                        <HeaderPrefix>{publicationTitle}</HeaderPrefix>
                        <StyledNightlyEditionHeading>
                            {normalizedEditionTitle} Edition
                        </StyledNightlyEditionHeading>
                    </HeaderContainer>
                    <StyledNightlyEditionTextContainerLeft>
                        <StyledNightlyEditionSubHeading>
                            In this Edition
                        </StyledNightlyEditionSubHeading>
                        <StyledNightlyEditionTeaser>
                            {edition.teaser}
                        </StyledNightlyEditionTeaser>
                    </StyledNightlyEditionTextContainerLeft>
                    <StyledNightlyEditionButtonContainerLeft>
                        <NightlyButton
                            variant={'default'}
                            text={`${normalizedEditionTitle} Edition`}
                            action={{
                                type: 'link',
                                to: edition.editionWebUrl,
                                onClick: () =>
                                    onEvent &&
                                    onEvent(
                                        createEditionSelectItemEvent({
                                            edition,
                                            collectionName:
                                                'TheNightlyOn_ReadEditionButton',
                                            product: Product.TheNightly,
                                            itemDetails: {
                                                category: 'thenightly_on_cta',

                                                linkText: 'Read Edition',
                                                linkUrl: edition.editionWebUrl,
                                            },
                                        }),
                                    ),
                            }}
                            color={'primary'}
                            fill={'filled'}
                            layout={{
                                shouldGrow: true,
                                alignSelf: 'stretch',
                            }}
                        />
                        <NightlyButton
                            variant={'default'}
                            text={'More Articles'}
                            action={{
                                type: 'link',
                                to: edition.primaryTopic
                                    ? `${edition.primaryTopic}`
                                    : '/on',
                                onClick: () =>
                                    onEvent &&
                                    onEvent(
                                        createEditionSelectItemEvent({
                                            edition,
                                            collectionName:
                                                'TheNightlyOn_MoreArticlesButton',
                                            product: Product.TheNightly,
                                            itemDetails: {
                                                category: 'thenightly_on_cta',

                                                linkText: 'More Articles',
                                                linkUrl: edition.primaryTopic
                                                    ? `${edition.primaryTopic}`
                                                    : '/on',
                                            },
                                        }),
                                    ),
                            }}
                            color={'primary'}
                            fill={'outline'}
                            layout={{
                                shouldGrow: true,
                                alignSelf: 'stretch',
                            }}
                        />
                    </StyledNightlyEditionButtonContainerLeft>
                    <StyledNightlyEditionTextContainer></StyledNightlyEditionTextContainer>
                    <StyledNightlyEditionPictureRight>
                        <WebLink
                            to={edition.editionWebUrl}
                            onClick={handleImageClick}
                        >
                            {mainImage && (
                                <picture>
                                    <source
                                        srcSet={mainImage.img}
                                        media={breakpointMax('sm', false)}
                                    />
                                    <source
                                        srcSet={mainImage.img}
                                        media={breakpoint('sm', false)}
                                    />
                                    <img
                                        src={mainImage.img}
                                        onError={({ currentTarget }) => {
                                            currentTarget.onerror = null
                                        }}
                                        alt={mainImage.alt}
                                        loading={
                                            lazyloadImages ? 'lazy' : undefined
                                        }
                                    />
                                </picture>
                            )}
                        </WebLink>
                    </StyledNightlyEditionPictureRight>
                </StyledNightlyEditionContentHome>
            </StyledNightlyEditionContainer>
        </>
    )
}
